import React, { useState, useEffect, useMemo } from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { DataTable, DataTableColumn } from "mantine-datatable";
import {
  StatCard,
  DateRangePicker,
  ReportHeader,
  ReportPanel,
} from "../../components";
import { DateRange } from "../../types";
import { ReportService } from "../../services/report-service";

// ==================== DATE HELPERS ====================

const today = new Date();
const formatDate = (d: Date) => d.toISOString().split("T")[0]; // YYYY-MM-DD

// ==================== ICONS ====================

const OrderIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
);

const TruckIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
  </svg>
);

const UserGroupIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

// ==================== HELPERS ====================

// ==================== LOADING SKELETON ====================

const LoadingSkeleton = ({ height = 300 }: { height?: number }) => (
  <div
    className="animate-pulse bg-gray-200 rounded-lg"
    style={{ height }}
  />
);

// ==================== COMPONENT ====================

export function OperationsDashboard() {
  const [dateRange, setDateRange] = useState<DateRange>({
    from: today,
    to: today,
  });
  const [selectedBranch, setSelectedBranch] = useState<string>("all");

  // ==================== API HOOKS ====================

  const {
    fetch: fetchOrdersByStatus,
    response: ordersByStatusResponse,
    loading: ordersByStatusLoading,
  } = ReportService.ordersByStatus();

  const {
    fetch: fetchRevenueByHour,
    response: revenueByHourResponse,
    loading: revenueByHourLoading,
  } = ReportService.revenueByHour();

  const {
    fetch: fetchDeliveryPerformance,
    response: deliveryPerformanceResponse,
    loading: deliveryPerformanceLoading,
  } = ReportService.deliveryPerformance();

  const {
    fetch: fetchRevenueByBranch,
    response: revenueByBranchResponse,
    loading: revenueByBranchLoading,
  } = ReportService.revenueByBranch();

  const {
    fetch: fetchBranchList,
    response: branchListResponse,
  } = ReportService.branchList();

  // ==================== FETCH ON DATE/BRANCH CHANGE ====================

  useEffect(() => {
    fetchBranchList();
  }, []);

  useEffect(() => {
    const params: any = {
      from_date: formatDate(dateRange.from),
      to_date: formatDate(dateRange.to),
    };
    if (selectedBranch !== "all") {
      params.branch_id = Number(selectedBranch);
    }

    fetchOrdersByStatus(params);
    fetchRevenueByHour(params);
    fetchDeliveryPerformance(params);
    fetchRevenueByBranch(params);
  }, [dateRange, selectedBranch]);

  // ==================== DERIVED DATA ====================

  const orderStatusData = useMemo(() => {
    const data = ordersByStatusResponse?.data;
    return {
      waiting_accept: data?.waiting_accept ?? 0,
      waiting_pick_up: data?.waiting_pick_up ?? 0,
      waiting_laundry: data?.waiting_laundry ?? 0,
      in_laundring: data?.in_laundring ?? 0,
      complete: data?.complete ?? 0,
      ready_delivery: data?.ready_delivery ?? 0,
      in_delivery: data?.in_delivery ?? 0,
      cancel: data?.cancel ?? 0,
    };
  }, [ordersByStatusResponse]);

  const hourlyOrdersData = useMemo(() => {
    const data = revenueByHourResponse?.data;
    if (!data || data.length === 0) {
      return Array(24).fill(0);
    }
    // Build a 24-hour array from API data
    const hourMap = new Map(data.map((item) => [item.hour, item.order_count]));
    return Array.from({ length: 24 }, (_, i) => hourMap.get(i) ?? 0);
  }, [revenueByHourResponse]);

  const shipperLeaderboard = useMemo(() => {
    const data = deliveryPerformanceResponse?.data;
    if (!data || data.length === 0) return [];
    return data.map((driver) => ({
      driver_id: driver.driver_id,
      total_deliveries: driver.total_deliveries,
      completed: driver.completed,
      cancelled: driver.cancelled,
      completionRate:
        driver.total_deliveries > 0
          ? Math.round((driver.completed / driver.total_deliveries) * 100)
          : 0,
    }));
  }, [deliveryPerformanceResponse]);

  const deliveryPerformanceData = useMemo(() => {
    const data = deliveryPerformanceResponse?.data;
    if (!data || data.length === 0) {
      return { onTime: 0, late: 0, early: 0, totalDeliveries: 0, totalCompleted: 0, totalCancelled: 0 };
    }
    const totalDeliveries = data.reduce((sum, d) => sum + d.total_deliveries, 0);
    const totalCompleted = data.reduce((sum, d) => sum + d.completed, 0);
    const totalCancelled = data.reduce((sum, d) => sum + d.cancelled, 0);
    const completedPct = totalDeliveries > 0 ? Math.round((totalCompleted / totalDeliveries) * 100) : 0;
    const cancelledPct = totalDeliveries > 0 ? Math.round((totalCancelled / totalDeliveries) * 100) : 0;
    const otherPct = Math.max(0, 100 - completedPct - cancelledPct);
    return {
      onTime: completedPct,
      late: cancelledPct,
      early: otherPct,
      totalDeliveries,
      totalCompleted,
      totalCancelled,
    };
  }, [deliveryPerformanceResponse]);

  // Branch list for dropdown + name map
  const branchOptions = useMemo(() => {
    const list: any[] = branchListResponse?.data || [];
    return list.filter((b: any) => b.id && b.name).map((b: any) => ({ id: b.id, name: b.name }));
  }, [branchListResponse]);

  const branchNameMap = useMemo(() => {
    const map: Record<number, string> = {};
    branchOptions.forEach((b) => { map[b.id] = b.name; });
    return map;
  }, [branchOptions]);

  const branchPerformance = useMemo(() => {
    const data = revenueByBranchResponse?.data;
    if (!data || data.length === 0) return [];
    return data.map((branch) => ({
      branch_id: branch.branch_id,
      name: branchNameMap[branch.branch_id] || `Chi nhánh #${branch.branch_id}`,
      orders: branch.order_count,
      revenue: branch.revenue,
    }));
  }, [revenueByBranchResponse, branchNameMap]);

  // ==================== COMPUTED VALUES ====================

  const totalActiveOrders =
    orderStatusData.waiting_accept +
    orderStatusData.waiting_pick_up +
    orderStatusData.waiting_laundry +
    orderStatusData.in_laundring +
    orderStatusData.ready_delivery +
    orderStatusData.in_delivery;

  const isAnyLoading =
    ordersByStatusLoading || revenueByHourLoading || deliveryPerformanceLoading || revenueByBranchLoading;

  // ==================== CHART CONFIGS ====================

  const orderFlowChartOptions: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      fontFamily: "Inter, sans-serif",
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "60%",
        borderRadius: 4,
        distributed: true,
      },
    },
    colors: ["#94a3b8", "#3b82f6", "#06b6d4", "#f97316", "#22c55e", "#a855f7", "#eab308", "#10b981", "#ef4444"],
    dataLabels: {
      enabled: true,
      style: { fontSize: "11px", colors: ["#374151"] },
      offsetY: -20,
    },
    xaxis: {
      categories: ["Chờ duyệt", "Chờ lấy", "Chờ giặt", "Đang giặt", "Hoàn thành", "Sẵn giao", "Đang giao", "Hủy"],
      labels: { style: { fontSize: "11px" } },
    },
    yaxis: {
      labels: { style: { fontSize: "11px" } },
    },
    legend: { show: false },
    tooltip: {
      y: { formatter: (val: number) => `${val} đơn` },
    },
  };

  const orderFlowChartSeries = [{
    name: "Đơn hàng",
    data: [
      orderStatusData.waiting_accept,
      orderStatusData.waiting_pick_up,
      orderStatusData.waiting_laundry,
      orderStatusData.in_laundring,
      orderStatusData.complete,
      orderStatusData.ready_delivery,
      orderStatusData.in_delivery,
      orderStatusData.cancel,
    ],
  }];

  const hourlyChartOptions: ApexOptions = {
    chart: {
      type: "area",
      toolbar: { show: false },
      fontFamily: "Inter, sans-serif",
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
        stops: [0, 100],
      },
    },
    colors: ["#3b82f6"],
    dataLabels: { enabled: false },
    xaxis: {
      categories: Array.from({ length: 24 }, (_, i) => `${i}:00`),
      labels: {
        style: { fontSize: "10px" },
        rotate: -45,
        rotateAlways: true,
      },
    },
    yaxis: {
      labels: { style: { fontSize: "11px" } },
    },
    tooltip: {
      y: { formatter: (val: number) => `${val} đơn` },
    },
  };

  const hourlyChartSeries = [{
    name: "Đơn hàng",
    data: hourlyOrdersData,
  }];

  const deliveryPieOptions: ApexOptions = {
    chart: {
      type: "donut",
      fontFamily: "Inter, sans-serif",
    },
    labels: ["Hoàn thành", "Đã hủy", "Khác"],
    colors: ["#22c55e", "#ef4444", "#3b82f6"],
    legend: {
      position: "bottom",
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val.toFixed(1)}%`,
    },
    plotOptions: {
      pie: {
        donut: {
          size: "65%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Tổng giao",
              formatter: () =>
                `${deliveryPerformanceData.onTime + deliveryPerformanceData.late + deliveryPerformanceData.early}%`,
            },
          },
        },
      },
    },
  };

  const deliveryPieSeries = [
    deliveryPerformanceData.onTime,
    deliveryPerformanceData.late,
    deliveryPerformanceData.early,
  ];

  // ==================== RENDER ====================

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <ReportHeader
        title="Báo Cáo Vận Hành"
        description="Giám sát quy trình xử lý đơn hàng và hiệu suất giao hàng"
      >
        <div className="flex items-center gap-4">
          <select
            value={selectedBranch}
            onChange={(e) => setSelectedBranch(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tất cả chi nhánh</option>
            {branchOptions.map((branch) => (
              <option key={branch.id} value={String(branch.id)}>
                {branch.name}
              </option>
            ))}
          </select>
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
          />
        </div>
      </ReportHeader>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          title="Đơn đang xử lý"
          value={ordersByStatusLoading ? "..." : totalActiveOrders}
          icon={<OrderIcon />}
          variant="primary"
          subtitle={`${orderStatusData.complete} đơn hoàn thành`}
        />
        <StatCard
          title="Đơn đang giao"
          value={ordersByStatusLoading ? "..." : orderStatusData.in_delivery}
          icon={<TruckIcon />}
          variant="warning"
          subtitle="Đang vận chuyển"
        />
        <StatCard
          title="Shipper đang hoạt động"
          value={deliveryPerformanceLoading ? "..." : shipperLeaderboard.length}
          icon={<UserGroupIcon />}
          variant="success"
          subtitle={`${orderStatusData.in_delivery} đơn đang giao`}
        />
      </div>

      {/* Real-time Order Status */}
      <ReportPanel title="Trạng Thái Đơn Hàng Theo Thời Gian Thực">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Order Flow Chart */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-4">Phân bổ theo trạng thái</h4>
            {ordersByStatusLoading ? (
              <LoadingSkeleton height={300} />
            ) : (
              <Chart
                options={orderFlowChartOptions}
                series={orderFlowChartSeries}
                type="bar"
                height={300}
              />
            )}
          </div>

          {/* Hourly Orders */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-4">Đơn hàng theo giờ (hôm nay)</h4>
            {revenueByHourLoading ? (
              <LoadingSkeleton height={300} />
            ) : (
              <Chart
                options={hourlyChartOptions}
                series={hourlyChartSeries}
                type="area"
                height={300}
              />
            )}
          </div>
        </div>
      </ReportPanel>

      {/* Delivery Performance */}
      <ReportPanel title="Hiệu Suất Giao Hàng">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="flex items-center justify-center">
            {deliveryPerformanceLoading ? (
              <LoadingSkeleton height={250} />
            ) : (
              <Chart
                options={deliveryPieOptions}
                series={deliveryPieSeries}
                type="donut"
                height={250}
              />
            )}
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center p-2 bg-green-50 rounded">
              <span className="text-sm text-gray-600">Hoàn thành</span>
              <span className="font-semibold text-green-600">{deliveryPerformanceData.onTime}%</span>
            </div>
            <div className="flex justify-between items-center p-2 bg-red-50 rounded">
              <span className="text-sm text-gray-600">Đã hủy</span>
              <span className="font-semibold text-red-600">{deliveryPerformanceData.late}%</span>
            </div>
          </div>
        </div>
      </ReportPanel>

      {/* Shipper Leaderboard */}
      <div className="grid grid-cols-1 gap-6">
        <ReportPanel title="Bảng Xếp Hạng Shipper">
          {deliveryPerformanceLoading ? (
            <LoadingSkeleton height={300} />
          ) : shipperLeaderboard.length === 0 ? (
            <div className="text-center text-gray-500 py-8">Không có dữ liệu shipper</div>
          ) : (
            <div className="space-y-3">
              {shipperLeaderboard.map((shipper, index) => (
                <div
                  key={shipper.driver_id}
                  className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm ${
                    index === 0 ? "bg-yellow-100 text-yellow-700" :
                    index === 1 ? "bg-gray-200 text-gray-700" :
                    index === 2 ? "bg-orange-100 text-orange-700" :
                    "bg-gray-100 text-gray-600"
                  }`}>
                    {index + 1}
                  </div>
                  <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-medium">
                    #{shipper.driver_id}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Tài xế #{shipper.driver_id}</p>
                    <p className="text-xs text-gray-500">{shipper.total_deliveries} đơn giao</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1">
                      <span className="font-medium text-gray-900">{shipper.completed} hoàn thành</span>
                    </div>
                    <p className="text-xs text-green-600">{shipper.completionRate}% tỷ lệ</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ReportPanel>
      </div>

      {/* Branch Performance */}
      <ReportPanel title="Hiệu Suất Theo Chi Nhánh">
        <div className="datatables">
          <DataTable
            fetching={revenueByBranchLoading}
            highlightOnHover
            withColumnBorders
            noRecordsText="Không có dữ liệu chi nhánh"
            className="whitespace-nowrap table-hover"
            records={branchPerformance}
            minHeight={200}
            columns={[
              {
                accessor: "name",
                title: "Chi nhánh",
              },
              {
                accessor: "orders",
                title: "Đơn hàng",
                textAlignment: "center",
              },
              {
                accessor: "revenue",
                title: "Doanh thu",
                textAlignment: "right",
                render: (row) => <span>{row.revenue.toLocaleString("vi-VN")} đ</span>,
              },
            ] as DataTableColumn<any>[]}
          />
        </div>
      </ReportPanel>
    </div>
  );
}

export default OperationsDashboard;
